import React from 'react'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import type { HolidayImage } from '@/src/types/holidays'

import HolidayImageView from '../holidayImage'

interface PhotoLightboxProps {
    images: HolidayImage[]
    activeIndex: number
    /** Pre-formatted, e.g. '3 / 24'. */
    counterLabel: string
    closeLabel: string
    previousLabel: string
    nextLabel: string
    onClose: () => void
    onPrevious: () => void
    onNext: () => void
    onSelect: (index: number) => void
    isMobile: boolean
}

/*
 * The scrim and control chrome are derived from the palette with `alpha` rather than written as
 * literals, so both brand builds stay on their own neutrals.
 */
const controlSx = (theme: Theme) => ({
    color: theme.palette.neutral.white,
    bgcolor: alpha(theme.palette.neutral.black, 0.45),
    '&:hover': { bgcolor: alpha(theme.palette.neutral.black, 0.65) }
})

const PhotoLightbox: React.FC<PhotoLightboxProps> = ({
    images,
    activeIndex,
    counterLabel,
    closeLabel,
    previousLabel,
    nextLabel,
    onClose,
    onPrevious,
    onNext,
    onSelect,
    isMobile
}) => {
    const active = images[activeIndex]
    if (!active) return null

    return (
        <Dialog
            open
            onClose={onClose}
            fullScreen
            slotProps={{
                paper: {
                    sx: (theme: Theme) => ({
                        bgcolor: alpha(theme.palette.neutral.black, 0.92),
                        backgroundImage: 'none'
                    })
                }
            }}
        >
            <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <IconButton
                    onClick={onClose}
                    aria-label={closeLabel}
                    sx={theme => ({
                        ...controlSx(theme),
                        position: 'absolute',
                        top: 16,
                        insetInlineEnd: 16,
                        zIndex: 2
                    })}
                >
                    <CloseIcon />
                </IconButton>

                {/* Stage */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: isMobile ? 1 : 8,
                        py: 2,
                        position: 'relative'
                    }}
                >
                    {images.length > 1 && (
                        <IconButton
                            onClick={onPrevious}
                            aria-label={previousLabel}
                            sx={theme => ({
                                ...controlSx(theme),
                                position: 'absolute',
                                insetInlineStart: isMobile ? 8 : 24
                            })}
                        >
                            <ChevronLeftIcon sx={{ '[dir=rtl] &': { transform: 'scaleX(-1)' } }} />
                        </IconButton>
                    )}

                    {/*
                     * The stage is a fixed box and the image is told to `contain`, so a portrait
                     * and a landscape photo both fill as much of it as they can without cropping.
                     */}
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& img': { objectFit: 'contain', width: 'auto', maxWidth: '100%', maxHeight: '100%' }
                        }}
                    >
                        <HolidayImageView image={active} lazy={false} isMobile={isMobile} />
                    </Box>

                    {images.length > 1 && (
                        <IconButton
                            onClick={onNext}
                            aria-label={nextLabel}
                            sx={theme => ({
                                ...controlSx(theme),
                                position: 'absolute',
                                insetInlineEnd: isMobile ? 8 : 24
                            })}
                        >
                            <ChevronRightIcon sx={{ '[dir=rtl] &': { transform: 'scaleX(-1)' } }} />
                        </IconButton>
                    )}
                </Box>

                <Typography
                    sx={{
                        textAlign: 'center',
                        color: 'neutral.white',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        pb: 1.5
                    }}
                >
                    {counterLabel}
                </Typography>

                {/* Thumbnail strip */}
                {images.length > 1 && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                            overflowX: 'auto',
                            px: 2,
                            pb: 'calc(16px + env(safe-area-inset-bottom))'
                        }}
                    >
                        {images.map((image, index) => (
                            <Box
                                key={image.url}
                                component="button"
                                type="button"
                                onClick={() => onSelect(index)}
                                aria-label={image.alt}
                                aria-current={index === activeIndex}
                                sx={{
                                    flexShrink: 0,
                                    width: isMobile ? 64 : 104,
                                    height: isMobile ? 48 : 72,
                                    p: 0,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: index === activeIndex ? 'primary.main' : 'transparent',
                                    opacity: index === activeIndex ? 1 : 0.55,
                                    transition: 'opacity 0.2s, border-color 0.2s',
                                    '&:hover': { opacity: 1 }
                                }}
                            >
                                <HolidayImageView image={image} isMobile={isMobile} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Dialog>
    )
}

export { PhotoLightbox }
export type { PhotoLightboxProps }
export default PhotoLightbox
